import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import API from './Api';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash, FaLock, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';

const SetNewPassword = () => {
    const navigate = useNavigate();
    const { uidb64, token } = useParams(); 

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Real-time password strength validation flags
    const hasMinLength = password.length >= 8;
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<> ]/.test(password);
    const isMatched = password && password === confirmPassword;

    const handleFormSubmitAction = async (e) => {
        e.preventDefault();
        if (!hasMinLength || !hasNumber || !hasSpecialChar) return;
        if (!isMatched) return;

        
        const userIdentifier = uidb64 || window.location.pathname.split('/')[3]; 

        if (!userIdentifier || userIdentifier === "undefined" || userIdentifier === "null") {
            toast.error("Error: User configuration token (UID) is missing from URL. Please reopen the link from your email.");
            return;
        }

        setLoading(true);
        try {
           
            console.log("Submitting with Identifier:", userIdentifier, "Token:", token);

            const res = await API.post('reset-password-confirm/', {
                uid: userIdentifier,    
                uidb64: userIdentifier,  
                token: token,
                password: password
            });

            if (res.data.status === "Success") {
                toast.success("Success! Password updated smoothly.");
                navigate('/TGSH/login');
            }
        } catch (err) {
            console.error("Network sync transmission error track:", err.response?.data);
            toast.error(err.response?.data?.message || "Failed to finalize new password entry.");
        } finally {
            setLoading(false);
        }
    };

    return (
        // 💡 FIX 1: Removed dark full-blur screen freeze. Using a modern crisp layout with subtle absolute positioning.
        <div className="min-h-screen w-full bg-[#F8FAFC] flex flex-col justify-center items-center font-sans relative antialiased p-4 select-none">
            
            {/* ─── DYNAMIC FIXED BRANDING NAVBAR ─── */}
            {/* 💡 FIX 2: Restored pure visibility so the absolute navigation row items stand out nicely */}
            <div className="absolute top-0 left-0 w-full bg-white border-b border-gray-200/80 px-6 py-3.5 flex justify-between items-center shadow-xs z-50">
                <div className="flex items-center gap-3">
                    {/* Circle logo branding constraint enforced cleanly */}
                    <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-green-400 to-blue-600 flex items-center justify-center text-white font-black text-xs shadow-sm select-none">
                        TGSH
                    </div>
                    <span className="text-xs font-bold uppercase tracking-widest text-gray-500 font-sans">
                        The Global School Of Happiness
                    </span>
                </div>
                <div className="flex items-center gap-4 text-gray-400 text-sm">
                    <span className="cursor-pointer hover:text-blue-900 transition-colors">✦ Support</span>
                </div>
            </div>

            {/* ─── CREATIVE PASSWORD UPDATE CENTER CARD ─── */}
            {/* 💡 FIX 3: Premium clean shadows, rounded grids, and fine spacing constraints */}
            <div className="bg-white w-full max-w-md p-8 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100 flex flex-col items-center mt-16 relative z-10 animate-in fade-in zoom-in-95 duration-300">
                
                {/* Embedded lock crown decoration */}
                <div className="w-12 h-12 bg-blue-50/60 text-blue-600 rounded-full flex items-center justify-center shadow-inner mb-4 border border-solid border-blue-100/30">
                    <FaLock size={16} />
                </div>

                <h2 className="text-2xl font-semibold font-serif text-gray-800 tracking-tight text-center mb-1">
                    Set New Password
                </h2>
                <p className="text-[11px] text-gray-400 uppercase font-bold tracking-wider text-center mb-6">
                    Please create a secure password
                </p>

                <form onSubmit={handleFormSubmitAction} className="w-full space-y-4">
                    
                    {/* Element A: New Password Input */}
                    <div className="flex flex-col space-y-1">
                        <label className="text-[10px] uppercase font-bold tracking-wider text-gray-400">
                            New Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative w-full">
                            <input 
                                type={showNewPassword ? "text" : "password"}
                                className="w-full p-2.5 pr-10 border border-gray-300 rounded-lg text-xs font-medium outline-none bg-gray-50/30 focus:border-blue-500 focus:bg-white transition-all shadow-2xs font-mono"
                                placeholder="Enter secure password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-gray-400 hover:text-blue-600 p-1 flex items-center justify-center transition-colors"
                            >
                                {showNewPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                            </button>
                        </div>
                    </div>

                    {/* Element B: Confirm Password Input */}
                    <div className="flex flex-col space-y-1">
                        <label className="text-[10px] uppercase font-bold tracking-wider text-gray-400">
                            Confirm Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative w-full">
                            <input 
                                type={showConfirmPassword ? "text" : "password"}
                                className="w-full p-2.5 pr-10 border border-gray-300 rounded-lg text-xs font-medium outline-none bg-gray-50/30 focus:border-blue-500 focus:bg-white transition-all shadow-2xs font-mono"
                                placeholder="Re-enter password to match"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer text-gray-400 hover:text-blue-600 p-1 flex items-center justify-center transition-colors"
                            >
                                {showConfirmPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                            </button>
                        </div>
                    </div>

                    {/* ─── DYNAMIC CRITERIA TRACKER BADGES ─── */}
                    <div className="bg-gray-50/70 border border-solid border-gray-200/60 rounded-xl p-4 space-y-2 mt-2 text-[11px] font-medium text-gray-500 shadow-inner">
                        <div className={`flex items-center gap-2.5 transition-colors ${hasMinLength ? 'text-green-600 font-bold' : ''}`}>
                            {hasMinLength ? <FaCheckCircle size={12} /> : <div className="w-1.5 h-1.5 rounded-full bg-gray-300 ml-1" />}
                            <span>At least 8 characters length matrix bounds</span>
                        </div>

                        <div className={`flex items-center gap-2.5 transition-colors ${hasNumber ? 'text-green-600 font-bold' : ''}`}>
                            {hasNumber ? <FaCheckCircle size={12} /> : <div className="w-1.5 h-1.5 rounded-full bg-gray-300 ml-1" />}
                            <span>Contains a registered numeric index entry (0-9)</span>
                        </div>

                        <div className={`flex items-center gap-2.5 transition-colors ${hasSpecialChar ? 'text-green-600 font-bold' : ''}`}>
                            {hasSpecialChar ? <FaCheckCircle size={12} /> : <div className="w-1.5 h-1.5 rounded-full bg-gray-300 ml-1" />}
                            <span>Special explicit symbolic asset character (!@#$%^&*)</span>
                        </div>

                        {confirmPassword && (
                            <div className={`flex items-center gap-2.5 pt-2 border-t border-dashed border-gray-200/80 ${isMatched ? 'text-teal-600 font-bold' : 'text-red-500 font-bold'}`}>
                                <FaExclamationCircle size={12} />
                                <span>{isMatched ? 'Security verification passwords match.' : 'Input payloads do not match yet.'}</span>
                            </div>
                        )}
                    </div>

                    {/* Dispatch Execution Button */}
                    <div className="pt-2">
                        <button
                            type="submit"
                            disabled={loading || !isMatched || !hasMinLength || !hasNumber || !hasSpecialChar}
                            className="w-full py-2.5 bg-blue-600 text-white font-bold text-xs uppercase tracking-widest rounded-xl hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-all border-none cursor-pointer shadow-sm active:scale-[0.99]"
                        >
                            {loading ? "Updating Credentials..." : "Set Password"}
                        </button>
                    </div>

                </form>

            </div>
        </div>
    );
};

export default SetNewPassword;